/**
 * AppointmentOptionsPanel — Office staff proposes up to 10 appointment
 * choices (date + time ONLY). The client picks one from the public portal.
 * Once the client picks, the options + chosen value become read-only.
 *
 * Business rule (Iteration 66): appointment options are date+time only.
 * No location, no notes, no price — those belong to the invoice, not the
 * appointment. The cap was raised from 3 to 10 because some embassies
 * issue many slots and the office needs the flexibility.
 */
import { useEffect, useState } from "react";
import { CalendarClock, Plus, Trash2, Lock, ShieldCheck, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";

const MAX_OPTIONS = 10;

export default function AppointmentOptionsPanel({ application, onChanged }) {
  const appId = application?.id || application?._id;
  const appNum = application?.application_number;
  const chosen = application?.appointment_chosen;
  const [options, setOptions] = useState([]);
  const [draft, setDraft] = useState([{ datetime: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!appNum) return;
    api.get(`/portal/track/${appNum}/appointment-options`)
      .then(({ data }) => setOptions(data.options || []))
      .catch(() => setOptions([]));
  }, [appNum]);

  if (!appId) return null;
  const isLocked = !!chosen;
  const hasExisting = options.length > 0;

  const submit = async () => {
    setBusy(true); setErr("");
    const payload = draft
      .filter((d) => (d.datetime || "").trim())
      .map((d) => ({ datetime: new Date(d.datetime).toISOString() }));
    if (payload.length === 0) { setErr("أضف موعداً واحداً على الأقل"); setBusy(false); return; }
    if (payload.length > MAX_OPTIONS) { setErr(`الحد الأقصى ${MAX_OPTIONS} مواعيد`); setBusy(false); return; }
    try {
      const { data } = await api.post(`/workspace/applications/${appId}/appointment-options`, { options: payload });
      setOptions(data.options || []);
      setDraft([{ datetime: "" }]);
      onChanged?.();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={css.card} data-testid="appointment-options-panel">
      <div style={css.head}>
        <CalendarClock size={20} strokeWidth={2.2} style={{ color: "#d4af37" }} />
        <h3 style={css.title}>اقتراح المواعيد للعميل</h3>
      </div>
      <p style={css.hint}>
        أضف <strong>1 إلى {MAX_OPTIONS} مواعيد</strong> (تاريخ ووقت فقط).
        عند اختيار العميل أحدها يصبح ملزماً ولا يستطيع تغييره
        (يُسجَّل IP والتوقيت). الفاتورة شغل مستقل في تبويب التسعير.
      </p>

      {hasExisting && (
        <div style={{ marginBottom: 14 }}>
          <div style={css.subtitle}>الخيارات المقترحة:</div>
          {options.map((o) => {
            const isChosen = chosen?.option_id === o.id;
            return (
              <div key={o.id} style={{
                ...css.optionRow,
                borderColor: isChosen ? "#16a34a" : "rgba(0,0,0,0.12)",
                background: isChosen ? "rgba(22,163,74,0.08)" : "transparent",
              }}>
                <div style={{ fontWeight: 800, fontSize: 13 }} dir="ltr">
                  {new Date(o.datetime).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", hour12: false })}
                </div>
                {isChosen && (
                  <div style={{ color: "#16a34a", fontWeight: 800, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                    <ShieldCheck size={14} /> اختاره العميل
                  </div>
                )}
              </div>
            );
          })}
          {isLocked && (
            <div style={css.lockedBox}>
              <Lock size={14} strokeWidth={2.4} />
              <div>
                <div style={{ fontWeight: 800 }}>تم التثبيت — لا تعديل بعد ذلك</div>
                <div style={{ fontSize: 10, opacity: 0.8, fontFamily: "monospace" }} dir="ltr">
                  {chosen.picked_at && new Date(chosen.picked_at).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", hour12: false })}
                  {chosen.client_ip ? ` • IP: ${chosen.client_ip}` : ""}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!isLocked && (
        <div style={{ marginTop: hasExisting ? 14 : 0 }}>
          <div style={css.subtitle}>{hasExisting ? "استبدال الخيارات بمجموعة جديدة:" : "أضف الخيارات:"}</div>
          {draft.map((d, i) => (
            <div key={i} style={css.draftRow}>
              <input
                type="datetime-local"
                value={d.datetime}
                onChange={(e) => setDraft((s) => s.map((x, idx) => idx === i ? { ...x, datetime: e.target.value } : x))}
                style={css.input}
                data-testid={`appt-datetime-${i}`}
              />
              {draft.length > 1 && (
                <button onClick={() => setDraft((s) => s.filter((_, idx) => idx !== i))} style={css.btnRemove} data-testid={`appt-remove-${i}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
          {draft.length < MAX_OPTIONS && (
            <button onClick={() => setDraft((s) => [...s, { datetime: "" }])} style={css.btnGhost} data-testid="appt-add-draft">
              <Plus size={14} strokeWidth={2.4} /> موعد آخر
            </button>
          )}
          {err && <div style={css.err}>{err}</div>}
          <button onClick={submit} disabled={busy} style={css.btnGold} data-testid="appt-submit">
            {busy ? <Loader2 size={14} className="vta-spin" /> : <CalendarClock size={14} strokeWidth={2.4} />}
            إرسال الخيارات للعميل
          </button>
        </div>
      )}
    </div>
  );
}

const css = {
  card: { background: "rgba(212,175,55,0.04)", border: "1.5px solid rgba(212,175,55,0.25)", borderRadius: 14, padding: 18, marginTop: 16 },
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  title: { margin: 0, fontSize: 15, fontWeight: 900, color: "#d4af37" },
  hint: { fontSize: 11, opacity: 0.8, marginBottom: 14, lineHeight: 1.7 },
  subtitle: { fontSize: 12, fontWeight: 800, marginBottom: 6, opacity: 0.85 },
  optionRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 10, marginBottom: 6, gap: 12 },
  lockedBox: { display: "flex", alignItems: "center", gap: 10, background: "rgba(22,163,74,0.10)", border: "1px solid #16a34a", color: "#16a34a", padding: "10px 12px", borderRadius: 10, marginTop: 10 },
  draftRow: { display: "grid", gridTemplateColumns: "1fr 36px", gap: 6, marginBottom: 6 },
  input: { padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", fontSize: 12, background: "rgba(255,255,255,0.04)", color: "inherit", boxSizing: "border-box", width: "100%", minWidth: 0 },
  btnGhost: { background: "transparent", color: "#d4af37", border: "1px dashed #d4af37", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4 },
  btnRemove: { background: "rgba(220,38,38,0.10)", color: "#dc2626", border: "1px solid #dc2626", padding: 4, borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" },
  btnGold: { background: "#d4af37", color: "#0a0a0a", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer", marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6 },
  err: { background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: 8, borderRadius: 8, fontSize: 11, marginTop: 8 },
};
